// Laying a week out (U91). Pure, so the arithmetic that decides where a block sits — and what happens
// when two meetings are at the same hour — is tested without a browser.
import type { Capture, WeekEvent } from './api.js';

export const DAY_MS = 86_400_000;

/**
 * The hours the grid draws (U98). All twenty-four of them, and the grid scrolls.
 *
 * It used to draw 07:00 to 20:00 and clamp anything outside. For an early call that meant a sliver at
 * the top wearing a time it did not have; for a late one it meant a block positioned at the very foot
 * of its column and then removed by the grid's own `overflow: hidden` -- present in the page, read
 * aloud by a screen reader, and not on the screen. Mike's 20:00 went that way on 2026-09-20. Drawing
 * the whole day costs a scrollbar and loses nothing.
 */
export const FIRST_HOUR = 0;
export const LAST_HOUR = 24;

/** The hour the grid is scrolled to when it opens: the working day, with the night above it. */
export const OPEN_AT_HOUR = 7;

/**
 * The hour the grid actually opens on: the working day, or the week's earliest meeting when that is
 * before it.
 *
 * Opening flat at 07:00 left a 06:30 call as a grey stub half under the day row -- visible only as a
 * box with no words in it, which is the same class of fault as the one U98 fixed, just softer: the
 * meeting is on the grid and the person cannot read it. An early call pulls the opening hour back to
 * itself; nothing else does.
 */
export function openAtHour(events: WeekEvent[], monday: string): number {
  const days = daysOf(monday);
  const first = days[0]!.getTime();
  const last = days[6]!.getTime() + DAY_MS;
  let earliest = OPEN_AT_HOUR;
  for (const e of events) {
    if (e.allDay) continue;
    const at = Date.parse(e.startsAt);
    if (Number.isNaN(at) || at < first || at >= last) continue;
    const d = new Date(at);
    earliest = Math.min(earliest, d.getHours());
  }
  return Math.max(FIRST_HOUR, earliest);
}

/**
 * How tall one hour is drawn. Here rather than only in the stylesheet because the component needs it
 * to know which blocks are too short to stack their lines (`isShort`) and where to scroll to.
 * `calendar.css` declares the same number as `--cal-hour`, and `week.test.ts` fails if the two drift.
 */
export const HOUR_PX = 46;

/** The whole drawn day, in pixels. */
export const DAY_PX = (LAST_HOUR - FIRST_HOUR) * HOUR_PX;

/** The block's padding, top and bottom together, as `calendar.css` sets it. */
export const BLOCK_PAD_PX = 6;
/** One line of type in a block, as `calendar.css` sets it. */
export const TITLE_LINE_PX = 15;

/**
 * A block too short to stack a time over a title.
 *
 * DERIVED, not chosen: exactly the height at which two lines and the padding stop fitting. It was a
 * round 30 before, and a 45-minute block -- 34.5px -- fell in the gap between that number and the 36px
 * two lines actually need, so it drew two lines into room for one and hid the overflow. A caught
 * arithmetic hole, not a taste.
 *
 * The fault this whole budget exists for: 7 of the 14 blocks on his own week of 2026-09-20 were events
 * the store holds with no length at all, clamped to the placement floor, showing a clock time and
 * nothing else because the name was clipped away.
 */
export const STACK_PX = BLOCK_PAD_PX + 2 * TITLE_LINE_PX;
export const isShort = (height: number): boolean => height * DAY_PX < STACK_PX;

/**
 * What a block has room to draw.
 *
 * Every line it draws is a line it must have room for, and this is the third time that has been got
 * wrong on this grid: first a fraction floor that made half his week a sliver with the name clipped
 * away, then a flat two-line clamp that put two lines into a one-hour block, and then a client's name
 * drawn under a title that had already used the room -- "IGame One Data / ... Optics Odds" cut off
 * mid-word on the screenshot of 2026-09-20. So the budget is counted once, here, from the block's own
 * height, and the component draws what it is told it can.
 *
 * The order is the order of importance: the time, the meeting's name, then the client's. A block too
 * short for two lines puts the time and the name on one (`short`), and there the NAME has the room --
 * a lane-split block 56px wide showed "11:00 I." and nothing else, which is a clock, not a meeting.
 */
export interface BlockRoom { short: boolean; titleLines: number; showCompany: boolean }

export function blockRoom(height: number, hasCompany: boolean): BlockRoom {
  const px = height * DAY_PX;
  if (px < STACK_PX) return { short: true, titleLines: 1, showCompany: false };
  const lines = Math.floor((px - BLOCK_PAD_PX) / TITLE_LINE_PX);
  const afterTime = Math.max(1, lines - 1);             // the time takes the first
  const showCompany = hasCompany && afterTime >= 2;
  return { short: false, titleLines: Math.max(1, Math.min(3, showCompany ? afterTime - 1 : afterTime)), showCompany };
}

/** Kept for the tests that measure the title alone. */
export const titleLines = (height: number, hasCompany = false): number => blockRoom(height, hasCompany).titleLines;

/** The shortest a block is ever drawn: enough for one line of type and its padding. */
export const MIN_PX = 18;

export interface Placed {
  event: WeekEvent;
  /** 0 to 6, Monday first. */
  day: number;
  /** Where the block starts and how tall it is, as a fraction of the drawn day. */
  top: number;
  height: number;
  /** Which of the overlapping blocks this is, and how many there are: side by side, as Outlook draws them. */
  lane: number;
  lanes: number;
}

/**
 * Midnight of the day a moment falls in, in the reader's own time. A calendar is read in the room it is
 * read in: a call at 09:00 in Monaco belongs to that Monday's nine o'clock, whatever UTC calls it.
 */
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** The days of the week beginning at `monday`, each at local midnight. */
export function daysOf(monday: string): Date[] {
  const m = new Date(monday);
  const first = new Date(m.getFullYear(), m.getMonth(), m.getDate());
  // Built by date rather than by adding 24 hours, so a week that crosses a clock change still has seven days.
  return Array.from({ length: 7 }, (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i));
}

/** Hours from the top of the drawn day, as a fraction of it; 0 is FIRST_HOUR and 1 is LAST_HOUR. */
function fraction(at: Date, day: Date): number {
  const hours = (at.getTime() - startOfDay(day)) / 3_600_000;
  return (hours - FIRST_HOUR) / (LAST_HOUR - FIRST_HOUR);
}

/**
 * Where each meeting sits in the week. A meeting that spans midnight is drawn on each day it touches,
 * because a week reads by column; one that starts before the first hour or ends after the last is
 * clamped, so an early call is a block at the top rather than nothing at all.
 */
export function place(events: WeekEvent[], monday: string): Placed[] {
  const days = daysOf(monday);
  const out: Placed[] = [];
  for (const [i, day] of days.entries()) {
    const dayStart = startOfDay(day);
    const dayEnd = startOfDay(new Date(dayStart + 36 * 3_600_000));   // the next midnight, clock change and all
    const here = events.filter((e) => {
      const s = Date.parse(e.startsAt);
      const t = Date.parse(e.endsAt);
      return !e.allDay && t > dayStart && s < dayEnd;
    });
    const laid = here.map((e) => {
      const top = Math.min(1, Math.max(0, fraction(new Date(e.startsAt), day)));
      const bottom = Math.min(1, Math.max(0, fraction(new Date(e.endsAt), day)));
      // A block is never shorter than a tappable row, or it cannot be clicked. Expressed against the
      // drawn day so it means the same thing as `isShort` whatever the hours are.
      return { event: e, day: i, top, height: Math.max(MIN_PX / DAY_PX, bottom - top), lane: 0, lanes: 1 };
    }).sort((a, b) => a.top - b.top || b.height - a.height);

    // Side by side where they overlap: a group is a run of blocks that touch, and each takes a lane.
    let group: typeof laid = [];
    const closeGroup = () => {
      for (const [n, item] of group.entries()) { item.lane = n; item.lanes = group.length; }
      group = [];
    };
    for (const item of laid) {
      if (group.length > 0 && item.top >= Math.max(...group.map((g) => g.top + g.height))) closeGroup();
      group.push(item);
    }
    closeGroup();
    out.push(...laid);
  }
  return out;
}

/** The all-day events of each day, which sit above the grid as Outlook puts them. */
export function allDayOf(events: WeekEvent[], monday: string): WeekEvent[][] {
  return daysOf(monday).map((day) => {
    const s = startOfDay(day);
    return events.filter((e) => e.allDay && Date.parse(e.endsAt) > s && Date.parse(e.startsAt) < s + DAY_MS);
  });
}

/** "09:00", on this computer's clock, which is the one the person reads the room by. */
export function clock(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? '' : new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** What a block says when Cobrain does not keep the meeting's words (R42). */
export const NO_WORDS = 'Busy';

/** Monday of the week that holds a day, at local midnight: the window the page asks the server for. */
export function mondayOf(d: Date): Date {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return m;
}

/** "22 to 28 September", or "29 September to 5 October" across a month's end. */
export function weekWords(monday: string): string {
  const days = daysOf(monday);
  const a = days[0]!;
  const b = days[6]!;
  const month = (d: Date) => new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(d);
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} to ${b.getDate()} ${month(b)}`
    : `${a.getDate()} ${month(a)} to ${b.getDate()} ${month(b)}`;
}

// ---------------------------------------------------------------------------------------------
// The day ahead (U95): what the night-before read is made of
// ---------------------------------------------------------------------------------------------

/**
 * The next day that actually has something on it, and what is on it.
 *
 * Not literally "tomorrow". Read at ten on a Monday morning, tomorrow is the wrong answer: the
 * three o'clock is what he is walking into. Read on a Sunday evening, the next day with anything on
 * it IS tomorrow, which is the case this was asked for. So the rule is the one that is right at
 * every hour -- the first day, from now on, that has a meeting still to start -- and the panel
 * names the day it found rather than assuming.
 *
 * Measured on the live store on 2026-09-20: his next fortnight holds 28 meetings, 7 of them with a
 * client. A day with one meeting on it is the normal case and a day with none is common, so this
 * returns null cheerfully and the panel has something honest to say about it.
 */
export function dayAhead(events: WeekEvent[], now: Date): { day: Date; events: WeekEvent[] } | null {
  const later = dedupe(events)
    .filter((e) => !e.allDay && !isOwnBlock(e) && Date.parse(e.startsAt) > now.getTime())
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const first = later[0];
  if (!first) return null;
  const day = new Date(first.startsAt);
  const midnight = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const next = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
  return { day: midnight, events: later.filter((e) => Date.parse(e.startsAt) < next) };
}

/**
 * One row per meeting, where the store holds two.
 *
 * His Tuesday carries "Modus <> iGame Media" twice at 15:00 -- two rows, two signal ids, one
 * meeting -- because the calendar ingest keys by iCalUId and an instance that came back without one
 * was inserted again (the fault B588 found and mostly undid; this pair survived it). The week grid
 * still draws both, because hiding a duplicate there would hide a real fault in the store. A list
 * you read to prepare is a different job: the same call twice is just noise, so here it is one.
 */
export function dedupe(events: WeekEvent[]): WeekEvent[] {
  const seen = new Set<string>();
  return events.filter((e) => {
    const key = `${e.startsAt}|${e.endsAt}|${e.title ?? ''}|${e.company?.id ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** "Tomorrow", "Today", or the day's own name -- whichever is true of the day this found. */
export function dayWords(day: Date, now: Date): string {
  const at = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((at(day) - at(now)) / DAY_MS);
  if (days === 0) return 'Later today';
  if (days === 1) return 'Tomorrow';
  return day.toLocaleDateString('en-GB', { weekday: 'long' });
}

/** "Monday 21 September", under the word above it. */
export const dayDate = (day: Date): string =>
  day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

/**
 * What the day ahead says when it has meetings but nothing to brief.
 *
 * This is the common case, not the edge: measured on the live store on 2026-09-20, 21 of his next
 * 28 meetings have nobody from outside iGame on them. An empty panel would read as "Cobrain found
 * nothing", which is a different claim from "there is nothing of that kind here" -- so it says how
 * many it read and why none of them has a brief (the house rule on empty states).
 */
export function nothingToBriefWords(meetings: number): string {
  const one = meetings === 1;
  return `${one ? 'One meeting' : `${meetings} meetings`}, and nobody from outside iGame on ${one ? 'it' : 'any of them'}, so there is nothing to brief.`;
}

/**
 * A block in his own day, rather than a meeting to prepare for.
 *
 * Read live on the evening of 2026-09-20, the panel led with "Later today" and offered a 20:00
 * medication reminder as "one meeting", while the thing he would actually have wanted that evening
 * was Monday's eleven o'clock with Media Pro. Correct by the rule and useless, which is the kind of
 * fault only his own calendar shows: the fixtures have one tidy meeting of every shape and nothing
 * a person puts in their calendar to remind themselves.
 *
 * So: nobody from outside, and fewer than two people on it, is his own block. It is skipped when
 * deciding WHICH day the panel lands on, and not listed. Deliberately narrow — anything with two
 * people, or any company at all, is still a meeting here, so the only thing this can hide is an
 * event with no attendees recorded and no company, which has no brief and cannot be prepared for
 * anyway. The week grid still draws every one of them; this is the reading list, not the calendar.
 *
 * Reversing it: drop the `!isOwnBlock(e)` clause in `dayAhead`.
 */
export const isOwnBlock = (e: WeekEvent): boolean => !e.company && e.people < 2;

/** "11:00–12:00", or just "11:00" when the store gives an event no length at all. */
export function spanWords(startsAt: string, endsAt: string): string {
  const from = clock(startsAt);
  const to = clock(endsAt);
  return from === to ? from : `${from}\u2013${to}`;
}

// ---------------------------------------------------------------------------------------------
// Which week the page opens on (U97)
// ---------------------------------------------------------------------------------------------

/**
 * The Monday the page lands on: the week holding the next meeting still to start.
 *
 * Read on the evening of Sunday 20 September 2026, the page opened on 14 to 20 September -- six days
 * already gone, today jammed against the right-hand edge, and the whole week ahead behind a press of
 * the arrow. The same is true every Friday evening and all Saturday, which is exactly when the
 * night-before read (U95) is done, on a page whose whole job is what is coming.
 *
 * So: the Monday of the week that holds the first meeting still to start. On a Sunday night that is
 * next week; on a Tuesday morning it is this one. The grid, the arrows and "This week" are unchanged,
 * so nothing is unreachable -- only the landing moves.
 *
 * `isOwnBlock` is applied for the same reason the day ahead applies it: a 20:00 medication reminder
 * is not the meeting the page should turn the week for. With nothing ahead at all it falls back to
 * the week holding today, which is the old behaviour and the right answer for an empty calendar.
 */
export function openingMonday(events: WeekEvent[] | null, now: Date): Date {
  const next = (events ?? [])
    .filter((e) => !e.allDay && !isOwnBlock(e) && Date.parse(e.startsAt) > now.getTime())
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  return mondayOf(next ? new Date(next.startsAt) : now);
}

// ---------------------------------------------------------------------------------------------
// What Cobrain will do about recording (U99)
// ---------------------------------------------------------------------------------------------

/**
 * The line that says whether Cobrain will be in the meeting.
 *
 * Mike, by pop-up on 2026-09-20, on learning that nothing anywhere said it: the week should carry a
 * mark on the block and a line in the brief. Until now the first he knew was in the panel once the
 * call had started, when the only thing left to do about it was ask the host.
 *
 * Every word here is a report of a row the bot lane wrote, never a judgement of its own, and the
 * absence of a row is said as an absence rather than dressed up as a "no": a meeting past the lane's
 * scheduling window simply has not been reached yet, and telling him it will not be recorded would be
 * false.
 */
export function captureWords(capture: Capture | null): { short: string; long: string; tone: 'on' | 'off' | 'open' } {
  if (!capture) return { short: 'Not decided', long: 'Cobrain has not decided about this one yet.', tone: 'open' };
  switch (capture.state) {
    case 'will': return { short: 'Cobrain will record', long: 'Cobrain will record this meeting.', tone: 'on' };
    case 'now': return { short: 'Recording', long: 'Cobrain is recording this meeting.', tone: 'on' };
    case 'done': return { short: 'Recorded', long: 'Cobrain recorded this meeting.', tone: 'on' };
    case 'no': return { short: 'Not recorded', long: reasoned('Cobrain will not record this meeting', capture.reason), tone: 'off' };
    case 'failed': return { short: 'Could not record', long: reasoned('Cobrain could not record this meeting', capture.reason), tone: 'off' };
    // BUGS.md M52: Mike by pop-up, 8 Oct 2026, "Mark them not held (Recommended)": "if nobody joined, the meeting shows as
    // not held on Teams instead of failed".
    case 'not_held': return { short: 'Not held on Teams', long: 'Not held on Teams: nobody joined it.', tone: 'off' };
  }
}

/**
 * The lane's reasons as a person says them (BUGS.md M50). The lane writes Recall's own codes and its own
 * tags ("timeout_exceeded_waiting_room", "daily_cap: 75 of 60 minutes", "recording.failed: ..."), and on
 * 8 Oct 2026 the page printed one of them raw. Each known code reads in plain words; a code that is not
 * here is not guessed at, and the sentence stands alone (Mike by pop-up, 8 Oct about 08:23 CEST, "All as
 * proposed"). Keyed by the reason's head, the part before any colon.
 */
const REASON_WORDS: Record<string, string> = {
  timeout_exceeded_waiting_room: 'nobody let it in from the waiting room',
  call_ended_by_platform_waiting_room_timeout: 'the meeting closed while it was still in the waiting room',
  bot_kicked_from_waiting_room: 'it was turned away at the waiting room',
  bot_kicked_from_call: 'it was removed from the call',
  timeout_exceeded_recording_permission_denied: 'the host did not allow recording',
  meeting_requires_sign_in: 'the meeting needs a sign-in',
  meeting_not_found: 'the meeting link did not work',
  meeting_link_invalid: 'the meeting link did not work',
  meeting_ended: 'the meeting had already ended',
  daily_cap: 'the daily recording limit was reached',
  recall_refused: 'the recording service turned it down',
  'recording.failed': 'the recording could not be processed',
  'transcript.failed': 'the recording could not be processed',
  realtime_probe: 'the live transcript could not start',
};

/** The plain words for the lane's reason, or null where it wrote none or wrote one this page does not know. */
export function reasonWords(reason: string | null): string | null {
  const head = (reason ?? '').split(':')[0]!.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(REASON_WORDS, head) ? REASON_WORDS[head]! : null;
}

/** "...: the daily recording limit was reached." The lane's reason in plain words, or the sentence alone. */
function reasoned(sentence: string, reason: string | null): string {
  const r = reasonWords(reason);
  return r ? `${sentence}: ${r}.` : `${sentence}.`;
}

/** Only a decision the lane has actually taken earns a mark on the grid; "not decided" is not news. */
export const marksTheBlock = (capture: Capture | null): boolean => capture !== null;

/**
 * What the week's head says it is showing.
 *
 * It used to read "16 meetings, 1 with a client" over a week whose sixteen included three medication
 * reminders and an ex-employee's time off, drawn twice. `isOwnBlock` already draws that distinction
 * for the day ahead and the week's own count did not use it, so the page called his own blocks
 * meetings. They are still drawn -- the grid is his calendar, not a filtered view of it -- and they
 * are no longer counted as something they are not.
 */
export function weekCount(events: WeekEvent[]): string {
  if (events.length === 0) return 'Nothing in your calendar this week.';
  const meetings = events.filter((e) => !e.allDay && !isOwnBlock(e));
  const clients = meetings.filter((e) => e.company).length;
  const mine = events.filter((e) => !e.allDay && isOwnBlock(e)).length;
  const allDay = events.filter((e) => e.allDay).length;
  if (meetings.length === 0) {
    const only = [mine > 0 ? `${mine} block${mine === 1 ? '' : 's'} of your own` : '', allDay > 0 ? `${allDay} all-day` : '']
      .filter(Boolean).join(' and ');
    return only ? `No meetings this week \u2014 ${only}.` : 'Nothing in your calendar this week.';
  }
  const rest = [mine > 0 ? `${mine} block${mine === 1 ? '' : 's'} of your own` : '', allDay > 0 ? `${allDay} all-day` : ''].filter(Boolean);
  return `${meetings.length} meeting${meetings.length === 1 ? '' : 's'}`
    + (clients > 0 ? `, ${clients} with a client` : '')
    + (rest.length > 0 ? ` \u00b7 ${rest.join(', ')}` : '');
}
